import type { ImageProps } from './Image'
import { Image } from './Image'
import { Source, getSrcSetChunk } from './Source'
import { isFileFormat } from '@helpers/core/isFileFormat'
import { getImageDescription } from '@helpers/core/getImageDescription'
import { getFileExtension } from '@helpers/core/getFileExtension'

export type ImageFormat = 'jpg' | 'jpeg' | 'png' | 'webp' | 'svg'
export type ImageFit = 'pad' | 'fill' | 'thumb' | 'scale' | 'crop'
export type ImageFocus =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top_right'
  | 'right_top'
  | 'right'
  | 'bottom_right'
  | 'right_bottom'
  | 'bottom'
  | 'bottom_left'
  | 'left_bottom'
  | 'left'
  | 'top_left'
  | 'left_top'
  | 'face'
  | 'faces'
export type FetchPriority = 'auto' | 'high' | 'low'

type SourceShape = {
  breakpoint: number
  src: string
  width: number
}

export type ResponsiveImageProps = {
  sources: SourceShape[]
  alt?: string
  className?: string
  style?: React.CSSProperties
  originalWidth?: number | null
  originalHeight?: number | null
  quality?: number
  ratio?: number
  bg?: string
  fit?: ImageFit
  focus?: ImageFocus
  onLoad?: () => void
  imageProps?: Partial<ImageProps>
  fetchPriority?: FetchPriority
}

type FallbackImageprops = {
  alt?: string
  width?: number
  height?: number
  fallbackSrc: string
  style?: React.CSSProperties
  loading?: 'eager' | 'lazy'
  onLoad?: () => void
  imageProps?: Partial<ImageProps>
  fetchPriority?: FetchPriority
}

const DEFAULT_QUALITY = 80

const getLast = <T,>(arr: T[] = []) => arr[arr.length - 1]

export const getImageDimensions = ({
  originalWidth,
  originalHeight,
  width,
}: {
  originalWidth?: number | null
  originalHeight?: number | null
  width: number | null
}) => {
  if (originalWidth && originalHeight && width) {
    const aspectRatio = originalWidth / originalHeight
    const height = Math.round(width / aspectRatio)

    return { width, height }
  }

  return { width }
}

const FallbackImage = ({
  alt,
  loading,
  onLoad,
  fallbackSrc,
  style,
  width,
  height,
  imageProps,
  fetchPriority,
}: FallbackImageprops) => {
  const formattedAlt = getImageDescription(alt)

  return (
    <Image
      {...imageProps}
      alt={formattedAlt}
      onLoad={onLoad}
      src={fallbackSrc}
      style={style}
      width={width}
      height={height}
      fetchPriority={fetchPriority}
    />
  )
}

const ResponsiveImage = ({
  alt = '',
  sources = [],
  className,
  originalWidth,
  originalHeight,
  fit,
  focus,
  quality = DEFAULT_QUALITY,
  ratio,
  bg,
  onLoad,
  style,
  imageProps,
  fetchPriority,
}: ResponsiveImageProps) => {
  const sortedSources = sources.sort((a, b) => b.breakpoint - a.breakpoint)

  // Bail if we don’t have `src` in every `source`.
  if (!sortedSources.every(({ src }) => Boolean(src))) {
    return null
  }

  // Extract file formats from the sources.
  const formats = sortedSources.map(({ src }) => {
    return getFileExtension(src) as ImageFormat
  })

  // Default shall be the smallest one, the last one.
  const defaultSource = getLast(sortedSources)
  const defaultFormat = getLast(formats)
  const defaultDimensions = getImageDimensions({
    originalWidth,
    originalHeight,
    width: defaultSource.width,
  })
  const fallbackSrc = getSrcSetChunk({
    format: defaultFormat,
    src: defaultSource.src,
    quality,
    width: defaultSource.width,
  })

  if (isFileFormat('svg', defaultSource.src)) {
    return (
      // Preserve the DOM structure, even when rendering SVGs.
      <div className={className}>
        <FallbackImage
          imageProps={imageProps}
          alt={alt}
          fallbackSrc={fallbackSrc}
          onLoad={onLoad}
          style={style}
          width={originalWidth as number}
          height={originalHeight as number}
          fetchPriority={fetchPriority}
        />
      </div>
    )
  }

  const getProps = (sourceProps: SourceShape, forcedFormat?: ImageFormat) => {
    const format =
      forcedFormat || (getFileExtension(sourceProps.src) as ImageFormat)

    const { height } = getImageDimensions({
      originalWidth,
      originalHeight,
      width: sourceProps.width,
    })

    return {
      // The 5 following props come right from the component, and they’ll get
      // applied to all the image sizes.
      fit,
      focus,
      quality,
      ratio,
      bg,
      format,
      height,
      originalWidth,
      // These props are the ones from the `sources` object and affect the
      // individual sizes. They should override the global values.
      ...sourceProps,
    }
  }

  return (
    <picture className={className}>
      {/* Render WebPs first, browsers pick the first format they support. */}
      {sortedSources.map(source => (
        <Source
          {...getProps(source, 'webp')}
          key={`${source.breakpoint}-${source.width}-webp`}
        />
      ))}

      {/* Then render the images on the format they were authored. */}
      {sortedSources.map(source => (
        <Source
          {...getProps(source)}
          key={`${source.breakpoint}-${source.width}`}
        />
      ))}

      {/* And finally the no-frills, regular fallback image. */}
      <FallbackImage
        imageProps={imageProps}
        alt={alt}
        fallbackSrc={fallbackSrc}
        onLoad={onLoad}
        style={style}
        width={defaultDimensions.width ? defaultDimensions.width : undefined}
        height={defaultDimensions?.height}
        fetchPriority={fetchPriority}
      />
    </picture>
  )
}

export { ResponsiveImage }
